import React, { useCallback, useState, useMemo, memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { chatAllUsersByIdSelector } from 'domain/chat/chatSelector';
import { useIntl } from 'react-intl';
import { useConfirm } from 'material-ui-confirm';

import TagBox from 'pages/common/Dialog/DialogTagsManage/components/TagBox';

import Dialog from 'components/mui/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TagSuggestionsAutocomplete from 'components/mui/Layouts/components/TagSuggestionsAutocomplete';
import TagsSearchInput from 'pages/common/Dialog/DialogTagsManage/components/TagsSearchInput';
import toast from 'components/Toast';

export const ROLES_FOR_CONFIRM = ['user', 'confidential-user', 'accountant'];

const DialogTagsManage = ({ initialTags, onCancel, open, documentID, dispatchAction }) => {
  const userList = useSelector(chatAllUsersByIdSelector);
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const confirm = useConfirm();
  const [currentValue, setCurrentValue] = useState('');
  const [isBusy, setIsBusy] = useState(false);
  const closeAfterSuccessRef = useRef(null);

  const isUser = useCallback((tag) => +!!userList.get(tag), [userList]);

  const checkUserForConfirm = useCallback(
    (tag) => {
      const user = userList.get(tag);

      return user && ROLES_FOR_CONFIRM.includes(user.role) && user.restricted ? user : null;
    },
    [userList],
  );

  const currentTags = useMemo(() => initialTags.filter((t) => t[0] !== '_'), [initialTags]);

  const sortedTags = useMemo(
    () => currentTags.sort((tagA, tagB) => isUser(tagB) - isUser(tagA)),
    [isUser, currentTags],
  );

  const isAddForbidden = useCallback(
    (tag) => {
      const currentTag = tag || currentValue;

      return (
        currentTags.includes(currentTag) || !currentTag || (typeof currentTag === 'string' && currentTag[0] === '_')
      );
    },
    [currentTags, currentValue],
  );

  const dispatchData = useCallback(
    (payload) => {
      setIsBusy(true);

      new Promise((resolve, reject) => {
        dispatch(dispatchAction({ ...payload, documentID, resolve, reject }));
      })
        .then(() => {
          toast.info(
            formatMessage({
              id: 'document.actions.tags.success',
              defaultMessage: 'Documents tags were updated',
            }),
          );
          if (closeAfterSuccessRef.current) {
            closeAfterSuccessRef.current = false;
            onCancel();
          }
        })
        .finally(() => {
          setIsBusy(false);
        });
    },
    [dispatch, dispatchAction, onCancel, formatMessage, documentID],
  );

  const handleSubmit = useCallback(
    (tag, isRemove = false) => {
      const tagIsUser = checkUserForConfirm(tag);

      const payload = isRemove ? { tagsToRemove: [tag], tagsToAdd: [] } : { tagsToRemove: [], tagsToAdd: [tag] };

      if (!tagIsUser) {
        dispatchData(payload);
      } else {
        confirm({
          title: formatMessage({
            id: 'confirm.userGainingDocumentAccess.title',
            defaultMessage: 'Access to document',
          }),
          description: (
            <>
              {!isRemove && (
                <Box mb={2}>
                  {formatMessage(
                    {
                      id: 'confirm.userAllowDocumentAccess.placeholder',
                      defaultMessage: `This action will enable ${tag} to view the document`,
                    },
                    {
                      usernames: <strong>{tag}</strong>,
                    },
                  )}
                </Box>
              )}
              {isRemove && (
                <Box>
                  {formatMessage(
                    {
                      id: 'confirm.userDenyDocumentAccess.placeholder',
                      defaultMessage: `This action will disable ${tag} to view the document`,
                    },
                    {
                      usernames: <strong>{tag}</strong>,
                    },
                  )}
                </Box>
              )}
            </>
          ),
          confirmationButtonProps: {
            color: isRemove ? 'error' : 'primary',
          },
        }).then(() => {
          dispatchData(payload);
        });
      }
    },
    [dispatchData, confirm, formatMessage, checkUserForConfirm],
  );

  const onChangeInput = useCallback((value) => {
    setCurrentValue(value);
  }, []);

  const handleAddition = useCallback(
    (tag) => {
      if (!isAddForbidden(tag)) {
        handleSubmit(tag);
      }

      setCurrentValue('');
    },
    [isAddForbidden, handleSubmit],
  );

  const handleDelete = useCallback(
    (tag) => {
      handleSubmit(tag, true);
    },
    [handleSubmit],
  );

  const handleOK = useCallback(() => {
    if (isAddForbidden(currentValue)) {
      onCancel();
    } else {
      handleAddition(currentValue);
      closeAfterSuccessRef.current = true;
    }
  }, [handleAddition, isAddForbidden, onCancel, currentValue]);

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={formatMessage({ id: 'modal.document.tags.title', defaultMessage: 'Document Tags' })}
      maxWidth="sm"
      withActions={false}
      withContent={false}
      slotProps={{
        paper: { sx: { overflow: 'inherit' } },
      }}
    >
      <DialogContent>
        <TagSuggestionsAutocomplete
          inputValue={currentValue}
          onInputChange={onChangeInput}
          onSelect={handleAddition}
          prefixOutputWithHashtag={false}
          renderInput={(params) => (
            <TagsSearchInput
              params={params}
              disabled={isAddForbidden() || isBusy}
              isBusy={isBusy}
              onAdd={() => handleAddition(currentValue)}
            />
          )}
        />
        <Stack gap={0.5} flexWrap="wrap" direction="row" mt={1}>
          {sortedTags.map((tag) => (
            <TagBox key={tag} tag={tag} onDelete={handleDelete} disabled={isBusy} />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="text">
          {formatMessage({ id: 'button.cancel', defaultMessage: 'Cancel' })}
        </Button>
        <Button onClick={handleOK} sx={{ minWidth: '10ch' }}>
          {formatMessage({ id: 'button.ok', defaultMessage: 'OK' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default memo(DialogTagsManage);
